import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { LTLAddressBookSnapshot, TempQuoteModel } from '@store/ltl/types';
import { customerStore, PickUpDropOffItem } from '@store/orders';
import {
  LineItemViewModel,
  OrderLineItem,
} from '@typeDefinitions/lineItemTypes';
import { ILoadEditViewModel } from '@typeDefinitions/loadTypes';
import { OperationTypeV4 } from '@typeDefinitions/operationTypes';
import { IOrderViewModel } from '@typeDefinitions/orderTypes';
import { LastUsedTopStopModel } from '@typeDefinitions/stopTypes';
import { BuildOrderFromQuote } from '@utils/ltl/LTLOrderBuilder';
import { printLog } from '@utils/utils';
import { DateTime } from 'luxon';

export const mergeOrderQuote = (
  quote: TempQuoteModel,
  existingOrder: IOrderViewModel,
  originSnapshot?: LTLAddressBookSnapshot,
  destinationSnapshot?: LTLAddressBookSnapshot,
): IOrderViewModel => {
  const newOrderFromQuote = BuildOrderFromQuote(
    quote,
    undefined,
    originSnapshot,
    destinationSnapshot,
  );
  const mergeOrder = {
    id: existingOrder.id,
    customerId: customerStore.customer.id,
    operationType:
      existingOrder.id <= 0
        ? 'Insert'
        : existingOrder.customerId === customerStore.customer.id
          ? ('None' as OperationTypeV4)
          : ('Update' as OperationTypeV4),
    //Note: setting the bid type to Spot Market if it is null so requoting/continuing
    //an order never leaves the required Bid Type field unset.
    bidType: Boolean(existingOrder.bidType)
      ? existingOrder.bidType
      : 'Spot Market',
    loads: mergeLoads(newOrderFromQuote.loads, existingOrder.loads),
    lineItems: mergeOrderLineItems(
      newOrderFromQuote.lineItems,
      existingOrder.lineItems,
    ),
    payments: existingOrder.payments,
    assignedId: existingOrder.assignedId,
    chargeCreditCardFee: existingOrder.chargeCreditCardFee,
    customerCredit: existingOrder.customerCredit,
    billingHold: existingOrder.billingHold,
    agingOut: existingOrder.agingOut,
    adjustments: existingOrder.adjustments,
    availableServices: existingOrder.availableServices,
    editable: existingOrder.editable,
    synched: existingOrder.synched,
    private: existingOrder.private,
    edi: existingOrder.edi,
    trackLoads: existingOrder.trackLoads,
    show214: existingOrder.show214,
    daysPastDue: existingOrder.daysPastDue,
    canVoidOrder: existingOrder.canVoidOrder,
    createdDate: existingOrder.createdDate,
    createdBy: existingOrder.createdBy,
    isDeleted: existingOrder.isDeleted,
    loadInformation: existingOrder.loadInformation,
    hideCustomerName: existingOrder.hideCustomerName,
    operationsId: existingOrder.operationsId,
    pointOfSaleInstructions: existingOrder.pointOfSaleInstructions,
  };
  return mergeOrder;
};

//handle order specific merging here
const mergeOrderLineItems = (
  quotedLineItems: OrderLineItem[],
  existingLineItems: OrderLineItem[],
) => {
  const originalLineItems = existingLineItems.map((x) => {
    return { ...x, operationType: 'Delete' } as OrderLineItem;
  });
  const newLines = [...quotedLineItems, ...originalLineItems];
  return newLines;
};

//handle load specific merging here
const mergeLoads = (
  quotedLoads: ILoadEditViewModel[],
  existingLoads: ILoadEditViewModel[],
) => {
  //Add logic here to associate each load with the one being quoted based on ID, will need to update LTLOrderBuilder to guard against
  // any potential issues with this
  const requotedLoad = existingLoads.find((x) => x.id == quotedLoads[0].id);
  if (requotedLoad) {
    const mergedLoad = mergeLoad(quotedLoads[0], requotedLoad);
    return [
      mergedLoad,
      ...existingLoads.filter((x) => x.id !== quotedLoads[0].id),
    ];
  }
  return existingLoads;
};

const mergeLoad = (
  quotedLoad: ILoadEditViewModel,
  existingLoad: ILoadEditViewModel,
) => {
  const newRequotedLoad = {
    ...existingLoad,
    items: getQuoteItems(existingLoad, quotedLoad),
    operationType: existingLoad.id <= 0 ? 'Insert' : 'Update',
    status: 'Assigned',
    ltlquoteId: quotedLoad.ltlQuote?.id ?? existingLoad.ltlquoteId,
    ltlQuote: {
      ...quotedLoad.ltlQuote,
      operationType: 'Insert',
      quoteItems: quotedLoad.ltlQuote?.ltlQuoteLoadItems,
    },
    quoteId: quotedLoad.quoteId,
    lineItems: mergeLineItems(quotedLoad.lineItems, existingLoad.lineItems),
    carrierId: quotedLoad.carrierId,
    // A carrier from a new quote is not saved yet, so it can still be cleared.
    ...(isFeatureFlagEnabled(
      FeatureFlags.EnableLoadDetailsCarrierBounceTracking,
    )() &&
      quotedLoad.carrierId !== existingLoad.carrierId && {
        isCarrierDirty: true,
      }),
    stops: getQuoteStops(quotedLoad.stops, existingLoad.stops),
  } as ILoadEditViewModel;
  return newRequotedLoad;
};
const normalizeZip = (zip: string | null | undefined): string =>
  (zip ?? '').trim().replace(/\s/g, '').toUpperCase().split('-')[0];

const normalizeState = (state: string | null | undefined): string =>
  (state ?? '').trim().toUpperCase();

const quoteZipChanged = (
  matchingQuoteStop: LastUsedTopStopModel | undefined,
  existingStop: LastUsedTopStopModel,
): matchingQuoteStop is LastUsedTopStopModel =>
  matchingQuoteStop?.zip !== undefined &&
  normalizeZip(matchingQuoteStop.zip) !== normalizeZip(existingStop.zip);

const quoteAddressMatches = (
  matchingQuoteStop: LastUsedTopStopModel | undefined,
  existingStop: LastUsedTopStopModel,
): matchingQuoteStop is LastUsedTopStopModel =>
  matchingQuoteStop !== undefined &&
  normalizeZip(matchingQuoteStop.zip) === normalizeZip(existingStop.zip) &&
  normalizeState(matchingQuoteStop.state) ===
    normalizeState(existingStop.state);

const mergeStopTimes = (
  quoteStops: LastUsedTopStopModel[] | undefined,
  matchingQuoteStop: LastUsedTopStopModel | undefined,
  existingStop: LastUsedTopStopModel,
) => {
  if (quoteZipChanged(matchingQuoteStop, existingStop)) {
    return {
      driverInTime: matchingQuoteStop.driverInTime?.toString(),
      driverOutTime: matchingQuoteStop.driverOutTime?.toString(),
      stopDateTime: matchingQuoteStop.stopDateTime?.toString(),
    };
  }
  const dates = getStopDates(quoteStops, existingStop);
  return {
    driverInTime: dates.driverInTime?.toString(),
    driverOutTime: dates.driverOutTime?.toString(),
    stopDateTime: dates.stopDateTime?.toString(),
  };
};

const mergeStopAddress = (
  matchingQuoteStop: LastUsedTopStopModel | undefined,
  existingStop: LastUsedTopStopModel,
) => {
  // When zip+state already match AND the quote stop has no address detail (address1
  // is empty, meaning no address book snapshot was used), preserve the user-entered
  // address data. If address1 is populated, the quote stop came from an address book
  // selection and its data should take precedence.
  const quoteStopFromAddressBook = Boolean(matchingQuoteStop?.address1);
  if (
    quoteAddressMatches(matchingQuoteStop, existingStop) &&
    !quoteStopFromAddressBook
  ) {
    return {
      locationName: existingStop.locationName,
      address1: existingStop.address1,
      address2: existingStop.address2,
      city: matchingQuoteStop.city ?? existingStop.city,
      state: matchingQuoteStop.state,
      zip: matchingQuoteStop.zip,
      countryCode: matchingQuoteStop.countryCode ?? existingStop.countryCode,
      contact: existingStop.contact,
      phone: existingStop.phone,
      email: existingStop.email,
      instructions: existingStop.instructions,
      description: existingStop.description,
      directions: existingStop.directions,
    };
  }
  return {
    locationName: matchingQuoteStop?.locationName ?? existingStop.locationName,
    address1: matchingQuoteStop?.address1 ?? existingStop.address1,
    address2: matchingQuoteStop?.address2 ?? existingStop.address2,
    city: matchingQuoteStop?.city ?? existingStop.city,
    state: matchingQuoteStop?.state ?? existingStop.state,
    zip: matchingQuoteStop?.zip ?? existingStop.zip,
    countryCode: matchingQuoteStop?.countryCode ?? existingStop.countryCode,
    contact: matchingQuoteStop?.contact ?? existingStop.contact,
    phone: matchingQuoteStop?.phone ?? existingStop.phone,
    email: matchingQuoteStop?.email ?? existingStop.email,
    instructions: matchingQuoteStop?.instructions ?? existingStop.instructions,
    description: matchingQuoteStop?.description ?? existingStop.description,
    directions: matchingQuoteStop?.directions ?? existingStop.directions,
  };
};

const getQuoteStops = (
  quoteStops: LastUsedTopStopModel[] | undefined,
  existingStops: LastUsedTopStopModel[] | undefined,
) => {
  return existingStops?.map((existingStop) => {
    const matchingQuoteStop = quoteStops?.find(
      (qs) =>
        (qs.pickUp && qs.pickUp === existingStop.pickUp) ||
        (qs.dropOff && qs.dropOff === existingStop.dropOff),
    );
    const times = mergeStopTimes(quoteStops, matchingQuoteStop, existingStop);
    const address = mergeStopAddress(matchingQuoteStop, existingStop);
    return {
      ...existingStop,
      ...address,
      ...times,
      operationType: existingStop.id <= 0 ? 'Insert' : 'Update',
    };
  });
};
const getStopDates = (
  quotedStops: LastUsedTopStopModel[] | undefined,
  existingStop: LastUsedTopStopModel,
) => {
  if (quotedStops === undefined) {
    return {
      driverInTime: existingStop.driverInTime,
      driverOutTime: existingStop.driverOutTime,
      stopDateTime: existingStop.stopDateTime,
    };
  }

  const quotedStop = quotedStops.find((x) => x.pickUp === existingStop.pickUp);
  printLog('quotedStop', quotedStop);
  if (quotedStop === undefined) {
    return {
      driverInTime: existingStop.driverInTime,
      driverOutTime: existingStop.driverOutTime,
      stopDateTime: existingStop.stopDateTime,
    };
  }

  const driverInTime = quotedStop.driverInTime
    ? quotedStop.driverInTime
    : existingStop.driverInTime;
  const driverOutTime = quotedStop.driverOutTime
    ? quotedStop.driverOutTime
    : existingStop.driverOutTime;
  return {
    driverInTime: getDriverInOrOutTime(driverInTime, existingStop.driverInTime),
    driverOutTime: getDriverInOrOutTime(
      driverOutTime,
      existingStop.driverOutTime,
    ),
    stopDateTime: quotedStop.stopDateTime
      ? quotedStop.stopDateTime
      : existingStop.stopDateTime,
  };
};
const getDriverInOrOutTime = (
  driverTime: string | null | undefined,
  existingDriverTime: string | null | undefined,
) => {
  let adjustedDriverInTime = driverTime;
  if (driverTime != null && existingDriverTime != null) {
    const formattedDriverTime = DateTime.fromISO(existingDriverTime, {
      zone: 'utc',
    });
    const q = DateTime.fromISO(driverTime);
    adjustedDriverInTime = q
      .toUTC()
      .set({
        hour: formattedDriverTime.hour,
        minute: formattedDriverTime.minute,
      })
      .toLocal()
      .set({
        day: q.day,
        month: q.month,
        year: q.year,
      })
      .toISO();
  }
  return adjustedDriverInTime;
};
const getQuoteItems = (
  existingLoad: ILoadEditViewModel,
  quotedLoad: ILoadEditViewModel,
) => {
  printLog('existingLoad', existingLoad);
  printLog('quotedLoad', quotedLoad);
  const dropOffId = existingLoad.stops?.find((x) => !x.pickUp)?.id;
  const pickupId = existingLoad.stops?.find((x) => x.pickUp)?.id;
  if (Number(dropOffId) > 0 && Number(pickupId)) {
    const quotedItems = quotedLoad.items?.map((x, i) => {
      return {
        ...x,
        id: -(i + 1),
        item: x.type ?? x.item,
        type: x.type ?? x.item,
        dropOffId: dropOffId,
        pickUpId: pickupId,
        operationType: 'Insert',
      };
    }) as PickUpDropOffItem[];
    const existingItems = existingLoad.items?.map((x) => {
      return {
        ...x,
        operationType: 'Delete',
      };
    }) as PickUpDropOffItem[];
    printLog('existingItems', existingItems);
    return [...quotedItems, ...existingItems];
  }
  return quotedLoad.items;
};

const mergeLineItems = (
  quotedLineItems?: LineItemViewModel[],
  existingLineItems?: LineItemViewModel[],
) => {
  const originalLineItems = existingLineItems?.map((x) => {
    return { ...x, operationType: 'Delete' } as LineItemViewModel;
  });
  if (originalLineItems && quotedLineItems) {
    return [...quotedLineItems, ...originalLineItems];
  }
  if (!originalLineItems && quotedLineItems) {
    return quotedLineItems;
  }
  return existingLineItems;
};
